import { GpuCharacter, type HueKey, type Persona } from './gpu-characters'

/**
 * Mission select artwork: one voxel illustration per workload, drawn with the
 * same glossy extruded-bead renderer as the GPU characters so the two screens
 * share a toy box but never share a figure.
 *
 * Map keys (see gpu-characters): b = body hue, l = highlight, s = shade,
 * p = dark screen, e = blinking eye, f = glow light, g = pulsing glow, m = metal.
 */

// Gaming: a pixel spaceship with a pulsing thruster.
const SHIP = [
  '......gg......',
  '......bb......',
  '.....blbb.....',
  '.....bppb.....',
  '....blpfbb....',
  '....bbppbb....',
  '...sbbbbbbs...',
  '..ssbbmmbbss..',
  '.sbbbbmmbbbbs.',
  'sbbsbbbbbbsbbs',
  'sb.sbbbbbbs.bs',
  's...ss..ss...s',
  '....gg..gg....',
  '.....g..g.....',
] as const

// Streaming: a broadcast mic on a stand, signal waves either side.
const MIC = [
  '..g........g..',
  '.g..bbbbbb..g.',
  'g..blllbbbb..g',
  'g..bmmmmmmb..g',
  'g..bbbbbbbb..g',
  '.g.bmmmmmmb.g.',
  '..gbbbbbbbbg..',
  '....bbbbbb....',
  '.s...ssss...s.',
  '.ss...mm...ss.',
  '..ssssmmssss..',
  '......mm......',
  '....ssssss....',
  '...ssssssss...',
] as const

// Testing: a bubbling lab flask.
const FLASK = [
  '.....g...g....',
  '...g....g.....',
  '.....mmmm.....',
  '......bb......',
  '......bb......',
  '.....bllb.....',
  '....blbbbb....',
  '...bbbffbbb...',
  '..bbgffbbgbb..',
  '.bbbbbbgbbbbb.',
  '.bfbbgbbbbfbb.',
  '.bbbbbbbbbbbb.',
  '..ssssssssss..',
] as const

// Light ML: a small brain sprouting a glowing seed.
const SPROUT = [
  '......gg......',
  '.....g..g.....',
  '......gg......',
  '.......s......',
  '...bbbbsbbb...',
  '..blbbbsbbbb..',
  '.blbsbbsbbsbb.',
  '.bbbbsbsbsbbb.',
  '.bsbbbbsbbbsb.',
  '.bbbsbbsbbbbb.',
  '..bbbbbsbbbb..',
  '...bbbbbbbb...',
  '.....mm.......',
  '....mmmm......',
] as const

// Content creation: a studio monitor mid-brushstroke, tablet and stylus.
const STUDIO = [
  '.mmmmmmmmmmmm.',
  '.mppppppppppm.',
  '.mpgggppppppm.',
  '.mppgggpfpppm.',
  '.mppppgggpppm.',
  '.mpfpppggpppm.',
  '.mppppppppppm.',
  '.mmmmmmmmmmmm.',
  '......mm....l.',
  '.....mmmm..lb.',
  '.bbbbbbbbbbbb.',
  'blllllbbbbbbsb',
  'bbbbbbbbbbbbsb',
  '.ssssssssssss.',
] as const

// AI inference: a robotic brain dome with a blinking visor.
const ROBO_BRAIN = [
  '....bbbbbb....',
  '..bbllbbbbbb..',
  '.blbbsbbsbbbb.',
  '.bbsbbbsbbsbb.',
  'bbbbbsbbbsbbbb',
  'bsbbbbsbbbbsbb',
  'mmmmmmmmmmmmmm',
  'mppppppppppppm',
  'mpeeppppppeepm',
  'mppppffffppppm',
  'mmmmmmmmmmmmmm',
  '..g..m..m..g..',
  '..g..m..m..g..',
  '.ggg......ggg.',
] as const

// AAA gaming: a futuristic controller.
const CONTROLLER = [
  '..ss........ss..',
  '.ssss......ssss.',
  '.bbbbbbbbbbbbbb.',
  'bllbbbbbbbbbbbgb',
  'blmbbbbffbbbbgbg',
  'bmmmbbbbbbbbbbgb',
  'bbmbbbbbbbbbbbbb',
  'bbbbbbbbbbbbbbbb',
  'bbbbbbssssbbbbbb',
  'bbbbbs....sbbbbb',
  '.bbbs......sbbb.',
  '..ss........ss..',
] as const

// ML training: a brain creature pressing a barbell overhead.
const LIFTER = [
  'mm..........mm',
  'mmmmmmmmmmmmmm',
  'mm..b....b..mm',
  '....b....b....',
  '...bbbbbbbb...',
  '..blbsbbsbbb..',
  '.blbbbsbbsbbb.',
  '.bbsbeebbeebb.',
  '.bbbbeebbeebb.',
  '.bbsbbbffbbsb.',
  '..bbbbbbbbbb..',
  '...bbbbbbbb...',
  '....ss..ss....',
  '...sss..sss...',
] as const

// Rendering: an isometric sculpted cube, lit top, hue left, shade right.
const CUBE = [
  '......ll......',
  '....llllll....',
  '..llllllllll..',
  'bbllllllllllss',
  'bbbbllllllssss',
  'bbbbbbllssssss',
  'bbbbbbbsssssss',
  'bbbbbbbsgsssss',
  'bbbbbbbsssssss',
  'bbbbbbbsssssss',
  '..bbbbbsssss..',
  '....bbbsss....',
  '......bs......',
] as const

// Heavy multitasking: three windows cascading over each other.
const WINDOWS = [
  'mmmmmmmm......',
  'bfpppppb......',
  'bppgpppb......',
  'bppssssssss...',
  'bppbfpppppb...',
  'bbbbppgpppb...',
  '...bppllllllll',
  '...bppbfpppppb',
  '...bbbbppggppb',
  '......bpgppppb',
  '......bppppgpb',
  '......bbbbbbbb',
] as const

export type MissionSize = 'normal' | 'wide' | 'full'

export interface MissionMeta {
  /** Playful mission name shown beside the real workload label. */
  title: string
  briefing: string
  hue: HueKey
  glow: HueKey
  map: readonly string[]
  /** Tile footprint in the mission grid from the sm breakpoint up. */
  size: MissionSize
  /** Atmosphere photograph (Unsplash licence), decorative. */
  photo: string
}

const photo = (id: string) => `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=900&q=70`

const MISSIONS: Record<string, MissionMeta> = {
  Gaming: {
    title: 'Pixel Run',
    briefing: 'Rent a cloud Windows PC by the hour and play on the screen you already have.',
    hue: 'lime',
    glow: 'lavender',
    map: SHIP,
    size: 'normal',
    photo: photo('1542751371-adc38448a05e'),
  },
  Streaming: {
    title: 'On Air',
    briefing: 'Run the game and the broadcast software together on one remote machine.',
    hue: 'ember',
    glow: 'lime',
    map: MIC,
    size: 'normal',
    photo: photo('1598550476439-6847785fcea6'),
  },
  Testing: {
    title: 'Lab Sim',
    briefing: 'Start a clean machine, try the build, shut it down again when you are done.',
    hue: 'mint',
    glow: 'lavender',
    map: FLASK,
    size: 'normal',
    photo: photo('1532094349884-543bc11b234d'),
  },
  'Light ML': {
    title: 'Spark Mind',
    briefing: 'Notebooks, small models and quick experiments on a GPU rented by the hour.',
    hue: 'lavender',
    glow: 'mint',
    map: SPROUT,
    size: 'normal',
    photo: photo('1555255707-c07966088b7b'),
  },
  'Content Creation': {
    title: 'Studio Cut',
    briefing: 'Edit, grade and export on a remote GPU machine instead of your own laptop.',
    hue: 'magenta',
    glow: 'lime',
    map: STUDIO,
    size: 'wide',
    photo: photo('1561070791-2526d30994b5'),
  },
  'AI Inference': {
    title: 'Neural Ping',
    briefing: 'Serve and test models on a GPU that you start and stop yourself.',
    hue: 'mint',
    glow: 'magenta',
    map: ROBO_BRAIN,
    size: 'wide',
    photo: photo('1677442136019-21780ecad995'),
  },
  'AAA Gaming': {
    title: 'Boss Raid',
    briefing: 'The demanding titles, on the bigger GPUs in the roster.',
    hue: 'ember',
    glow: 'mint',
    map: CONTROLLER,
    size: 'wide',
    photo: photo('1511512578047-dfb367046420'),
  },
  'ML Training': {
    title: 'Deep Grind',
    briefing: 'Longer training runs on the GPUs listed for them.',
    hue: 'magenta',
    glow: 'mint',
    map: LIFTER,
    size: 'normal',
    photo: photo('1620712943543-bcc4688e7485'),
  },
  Rendering: {
    title: 'Polygon Forge',
    briefing: 'Hand the render queue to a remote GPU and keep your own machine free.',
    hue: 'lavender',
    glow: 'lime',
    map: CUBE,
    size: 'normal',
    photo: photo('1618005182384-a83a8bd57fbe'),
  },
  'Heavy Multitasking': {
    title: 'Hydra Mode',
    briefing: 'Extra vCPUs and memory for many heavy apps open at once.',
    hue: 'lime',
    glow: 'magenta',
    map: WINDOWS,
    size: 'full',
    photo: photo('1593642632559-0c6d3fc62b89'),
  },
}

const FALLBACK_HUES: HueKey[] = ['lime', 'lavender', 'mint', 'magenta', 'ember']
const FALLBACK_ART = [SHIP, FLASK, CUBE, SPROUT, CONTROLLER]

/** Artwork and copy for a workload; one added to the catalogue later gets a generic mission. */
export function missionFor(workload: string, index: number): MissionMeta {
  return (
    MISSIONS[workload] ?? {
      title: workload,
      briefing: `The GPUs the catalogue lists for ${workload}.`,
      hue: FALLBACK_HUES[index % FALLBACK_HUES.length],
      glow: 'lime',
      map: FALLBACK_ART[index % FALLBACK_ART.length],
      size: 'normal',
      photo: MISSIONS.Gaming.photo,
    }
  )
}

export function MissionArt({
  mission,
  uid,
  title,
  className,
}: {
  mission: MissionMeta
  /** Unique per instance on the page, so gradient ids never collide. */
  uid: string
  title?: string
  className?: string
}) {
  const persona: Persona = {
    codename: mission.title,
    role: 'Mission',
    tagline: mission.briefing,
    hue: mission.hue,
    glow: mission.glow,
    map: mission.map,
  }
  return <GpuCharacter persona={persona} uid={uid} title={title} className={className} />
}
